// TraceroutePanel.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import axios from "./axiosConfig";

/* =========================
   Types
   ========================= */

interface HopNode {
  ip: string;
  rtt_ms?: number;
  timeout?: boolean;
}

interface Hop {
  index: number;
  nodes: HopNode[];
}

interface TraceResult {
  target: string;
  timestamp: string;
  hops: Hop[];
}

// Align with ApiSourceForm.tsx test response contract (subset)
type TestResponse = {
  ok: boolean;
  status?: number;
  latency_ms?: number;
  error?: string;

  body?: string;
  raw?: string;

  content_type?: string;
  truncated?: boolean;
  is_json?: boolean;
  json?: any;
};

export type TraceroutePanelData = {
  title?: string;

  // Only allow user to input API URL (full URL or relative path).
  // Example:
  // http://172.17.40.156:18090/traceroute?target=eu.obscpaas.com&proto=udp
  apiEndpoint?: string;

  autoRun?: boolean;
  autoRefreshSeconds?: number;

  // Optional cache for display; source of truth is apiEndpoint query
  target?: string;

  lastResult?: TraceResult | null;
};

interface TraceroutePanelProps {
  data?: TraceroutePanelData;
  onPanelDataChange?: (next: TraceroutePanelData) => void;
}

/* =========================
   Layout constants
   ========================= */

const HOP_GAP = 120;
const NODE_GAP = 50;
const NODE_RADIUS = 12;

const TIMEOUT_NODE_FILL = "#cccccc";
const NORMAL_NODE_FILL = "#4a90e2";
const HIGH_RTT_NODE_FILL = "#e24a4a";

const TIMEOUT_TEXT_COLOR = "#777777";
const HIGH_RTT_THRESHOLD_MS = 20;

// Default placeholder only (user should input full url)
const DEFAULT_API_ENDPOINT_PLACEHOLDER =
  "http://127.0.0.1:18090/traceroute?target=google.com&proto=udp";

type TooltipState = {
  visible: boolean;
  x: number;
  y: number;
  hopIndex: number;
  node: HopNode;
  fill: string;
  isTimeout: boolean;
};

/* =========================
   Helpers
   ========================= */

function safeTrim(v: any) {
  return String(v ?? "").trim();
}

function isAbsoluteUrl(s: string) {
  return /^https?:\/\//i.test(s);
}

function toAbsoluteUrlMaybe(raw: string) {
  const v = safeTrim(raw);
  if (!v) return "";
  if (isAbsoluteUrl(v)) return v;

  // relative -> absolute (same origin)
  try {
    return new URL(v, window.location.origin).toString();
  } catch {
    return v;
  }
}

function parseQueryFromEndpoint(raw: string): { target: string; proto: string; port: string } {
  const v = safeTrim(raw);
  if (!v) return { target: "", proto: "", port: "" };

  try {
    const u = isAbsoluteUrl(v) ? new URL(v) : new URL(v, window.location.origin);
    const target = safeTrim(u.searchParams.get("target") || "");
    const proto = safeTrim(u.searchParams.get("proto") || "");
    const port = safeTrim(u.searchParams.get("port") || "");
    return { target, proto, port };
  } catch {
    // fallback: regex parse
    const pick = (key: string) => {
      const m = v.match(new RegExp(`[?&]${key}=([^&]+)`, "i"));
      if (!m) return "";
      try {
        return safeTrim(decodeURIComponent(m[1]));
      } catch {
        return safeTrim(m[1]);
      }
    };
    return { target: pick("target"), proto: pick("proto"), port: pick("port") };
  }
}

function getBodyFromTestResponse(tr: TestResponse | null): string {
  if (!tr) return "";
  if (typeof tr.body === "string" && tr.body.trim()) return tr.body;
  if (typeof tr.raw === "string" && tr.raw.trim()) return tr.raw;
  return "";
}

function tryParseJSON(raw: string): { ok: boolean; value?: any; error?: string } {
  try {
    return { ok: true, value: JSON.parse(raw) };
  } catch (e: any) {
    return { ok: false, error: e?.message || "JSON parse failed" };
  }
}

function isValidTraceResult(x: any): x is TraceResult {
  if (!x || typeof x !== "object") return false;
  if (typeof x.target !== "string") return false;
  if (typeof x.timestamp !== "string") return false;
  if (!Array.isArray(x.hops)) return false;
  return true;
}

/* =========================
   Component
   ========================= */

const TraceroutePanel: React.FC<TraceroutePanelProps> = ({ data: panelData, onPanelDataChange }) => {
  const initialEndpoint = safeTrim(panelData?.apiEndpoint) || "";

  const [apiEndpoint, setApiEndpoint] = useState<string>(initialEndpoint);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only initialize once from lastResult (avoid rollback)
  const [traceData, setTraceData] = useState<TraceResult | null>(() => panelData?.lastResult ?? null);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  const persistPanelData = useCallback(
    (patch: Partial<TraceroutePanelData>) => {
      if (!onPanelDataChange) return;
      const merged: TraceroutePanelData = { ...(panelData || {}), ...patch };
      onPanelDataChange(merged);
    },
    [onPanelDataChange, panelData]
  );

  // Sync endpoint from dashboard -> local state
  useEffect(() => {
    const next = safeTrim(panelData?.apiEndpoint);
    if (!next) return;
    setApiEndpoint((prev) => (prev === next ? prev : next));
  }, [panelData?.apiEndpoint]);

  const parsed = useMemo(() => parseQueryFromEndpoint(apiEndpoint), [apiEndpoint]);

  const title = useMemo(() => {
    const t = safeTrim(panelData?.title);
    if (t) return t;
    if (parsed.target) return `Traceroute to ${parsed.target}`;
    return "Link Probe - Traceroute Viewer";
  }, [panelData?.title, parsed.target]);

  const clampTooltipPos = useCallback((clientX: number, clientY: number) => {
    const el = wrapperRef.current;
    if (!el) return { x: 0, y: 0 };

    const rect = el.getBoundingClientRect();
    const TIP_W = 260;
    const TIP_H = 110;

    let x = clientX - rect.left + 12;
    let y = clientY - rect.top + 12;

    const maxX = Math.max(8, rect.width - TIP_W - 8);
    const maxY = Math.max(8, rect.height - TIP_H - 8);

    x = Math.max(8, Math.min(x, maxX));
    y = Math.max(8, Math.min(y, maxY));

    return { x, y };
  }, []);

  const showTooltip = useCallback(
    (e: React.MouseEvent, hopIndex: number, node: HopNode, fill: string) => {
      const isTimeout = !!node.timeout;
      const { x, y } = clampTooltipPos(e.clientX, e.clientY);

      setTooltip({
        visible: true,
        x,
        y,
        hopIndex,
        node,
        fill,
        isTimeout,
      });
    },
    [clampTooltipPos]
  );

  const moveTooltip = useCallback(
    (e: React.MouseEvent) => {
      setTooltip((prev) => {
        if (!prev?.visible) return prev;
        const { x, y } = clampTooltipPos(e.clientX, e.clientY);
        return { ...prev, x, y };
      });
    },
    [clampTooltipPos]
  );

  const hideTooltip = useCallback(() => setTooltip(null), []);

  const handleRun = useCallback(async () => {
    const endpoint = safeTrim(apiEndpoint);
    if (!endpoint) {
      setError("API endpoint is required.");
      return;
    }

    const q = parseQueryFromEndpoint(endpoint);
    if (!q.target) {
      setError('Missing "target" in API endpoint. Example: ?target=eu.obscpaas.com&proto=udp');
      return;
    }

    const absoluteUrl = toAbsoluteUrlMaybe(endpoint);

    setError(null);
    setLoading(true);

    try {
      // IMPORTANT: Use backend proxy test API to avoid browser CORS / mixed content
      const res = await axios.post<TestResponse>("/api/api-sources/test", { url: absoluteUrl });
      const tr = res.data;

      if (!tr?.ok) {
        const msg = tr?.error || "Traceroute API test failed.";
        setError(msg);
        setTraceData(null);
        persistPanelData({
          apiEndpoint: endpoint,
          target: q.target,
          lastResult: null,
        });
        return;
      }

      // 1) Prefer backend parsed JSON
      let payload: any = null;

      if (tr.is_json && tr.json !== undefined && tr.json !== null) {
        payload = tr.json;
      } else {
        const body = getBodyFromTestResponse(tr);
        if (!body) {
          setError("Test OK but no response body returned by backend. Please return 'raw' or 'body' in test API.");
          setTraceData(null);
          persistPanelData({ apiEndpoint: endpoint, target: q.target, lastResult: null });
          return;
        }
        const parsedJson = tryParseJSON(body);
        if (!parsedJson.ok) {
          setError(`Test OK but response is not JSON: ${parsedJson.error}`);
          setTraceData(null);
          persistPanelData({ apiEndpoint: endpoint, target: q.target, lastResult: null });
          return;
        }
        payload = parsedJson.value;
      }

      if (!isValidTraceResult(payload)) {
        setError("Response JSON is not a valid TraceResult (expected: {target,timestamp,hops}).");
        setTraceData(null);
        persistPanelData({ apiEndpoint: endpoint, target: q.target, lastResult: null });
        return;
      }

      setTraceData(payload);
      persistPanelData({
        apiEndpoint: endpoint,
        target: q.target,
        lastResult: payload,
        title: `Traceroute to ${q.target}`,
      });
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || "Network error.";
      setError(msg);
      setTraceData(null);
      persistPanelData({
        apiEndpoint: safeTrim(apiEndpoint),
        target: parseQueryFromEndpoint(apiEndpoint).target,
        lastResult: null,
      });
    } finally {
      setLoading(false);
    }
  }, [apiEndpoint, persistPanelData]);

  // ===== Auto run once (key based on endpoint+target) =====
  const lastAutoRunKeyRef = useRef<string>("");
  useEffect(() => {
    if (!panelData?.autoRun) return;

    const ep = safeTrim(apiEndpoint);
    if (!ep) return;

    const q = parseQueryFromEndpoint(ep);
    if (!q.target) return;

    const key = `autorun:${ep}:${q.target}`;
    if (lastAutoRunKeyRef.current === key) return;

    lastAutoRunKeyRef.current = key;
    handleRun();
  }, [panelData?.autoRun, apiEndpoint, handleRun]);

  // ===== Auto refresh (no overlap) =====
  const runningRef = useRef<boolean>(false);
  useEffect(() => {
    const secondsRaw = Number(panelData?.autoRefreshSeconds || 0);
    const enabled = secondsRaw > 0;
    if (!enabled) return;

    const seconds = Math.max(5, Math.floor(secondsRaw));

    const timer = setInterval(async () => {
      if (runningRef.current) return;
      runningRef.current = true;
      try {
        await handleRun();
      } finally {
        runningRef.current = false;
      }
    }, seconds * 1000);

    return () => clearInterval(timer);
  }, [panelData?.autoRefreshSeconds, handleRun]);

  // ===== SVG layout =====
  const layout = useMemo(() => {
    if (!traceData || !traceData.hops || traceData.hops.length === 0) return null;

    const hops: Hop[] = [...traceData.hops].sort((a, b) => a.index - b.index);

    const hopCount = hops.length;
    const maxNodesPerHop = Math.max(...hops.map((h) => h.nodes.length || 1));

    const width = Math.max(hopCount * HOP_GAP + 80, 200);
    const height = Math.max(maxNodesPerHop * NODE_GAP + 80, 200);

    const positions: Record<string, { x: number; y: number }> = {};
    hops.forEach((hop, hopIdx) => {
      const x = 40 + hopIdx * HOP_GAP;

      const nodeCount = hop.nodes.length || 1;
      const centerY = height / 2;
      const totalHeight = (nodeCount - 1) * NODE_GAP;

      hop.nodes.forEach((_, nodeIdx) => {
        const y = centerY - totalHeight / 2 + nodeIdx * NODE_GAP;
        positions[`${hop.index}-${nodeIdx}`] = { x, y };
      });
    });

    return { hops, width, height, positions };
  }, [traceData]);

  const displayEndpoint = safeTrim(apiEndpoint);

  return (
    <div>
      <h5>{title}</h5>

      <Form
        onSubmit={(e) => {
          e.preventDefault();
          handleRun();
        }}
      >
        <Row className="align-items-end">
          <Col xs={12} md={8}>
            <Form.Group controlId="tracerouteApiEndpoint">
              <Form.Label>API endpoint (must include target=...)</Form.Label>
              <Form.Control
                type="text"
                value={apiEndpoint}
                onChange={(e) => setApiEndpoint(e.target.value)}
                onBlur={() => {
                  const ep = safeTrim(apiEndpoint);
                  setApiEndpoint(ep);
                  persistPanelData({ apiEndpoint: ep, target: parseQueryFromEndpoint(ep).target });
                }}
                placeholder={DEFAULT_API_ENDPOINT_PLACEHOLDER}
              />
              <Form.Text className="text-muted">
                Current: <span style={{ fontFamily: "monospace" }}>{displayEndpoint || "(empty)"}</span>
              </Form.Text>
            </Form.Group>
          </Col>

          <Col xs={12} md={4} className="mt-3 mt-md-0">
            <Form.Group controlId="tracerouteTargetReadonly">
              <Form.Label>Target (parsed)</Form.Label>
              <Form.Control
                type="text"
                value={parsed.target}
                readOnly
                placeholder='e.g. ?target=eu.obscpaas.com&proto=udp'
              />
              <Form.Text className="text-muted">
                proto: <span style={{ fontFamily: "monospace" }}>{parsed.proto || "(default)"}</span>
                <span className="ms-2">
                  port: <span style={{ fontFamily: "monospace" }}>{parsed.port || "(default)"}</span>
                </span>
              </Form.Text>
            </Form.Group>
          </Col>

          <Col xs={12} md="auto" className="mt-3 mt-md-0">
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                  Running...
                </>
              ) : (
                "Run traceroute"
              )}
            </Button>
          </Col>
        </Row>
      </Form>

      {error && (
        <Alert variant="danger" className="mt-3">
          {error}
        </Alert>
      )}

      {traceData && !layout && !loading && (
        <Alert variant="warning" className="mt-3">
          No hops returned for this target.
        </Alert>
      )}

      {layout && (
        <div className="mt-3">
          <div className="mb-2">
            <strong>API:</strong> <span style={{ fontFamily: "monospace" }}>{displayEndpoint}</span>
            <span className="ms-3">
              <strong>Target:</strong> {traceData?.target}{" "}
              <small className="text-muted ms-2">{new Date(traceData!.timestamp).toLocaleString()}</small>
            </span>
          </div>

          <div ref={wrapperRef} style={{ position: "relative" }}>
            <div
              style={{
                border: "1px solid #ddd",
                borderRadius: 8,
                padding: 8,
                overflowX: "auto",
              }}
              onMouseLeave={hideTooltip}
            >
              <svg width={layout.width} height={layout.height} style={{ display: "block" }}>
                {layout.hops.map((hop) => {
                  const firstNodeKey = `${hop.index}-0`;
                  const pos = layout.positions[firstNodeKey];
                  if (!pos) return null;
                  return (
                    <g key={`hop-label-${hop.index}`}>
                      <text
                        x={pos.x}
                        y={20}
                        textAnchor="middle"
                        fontSize={12}
                        fill="#333"
                        style={{ pointerEvents: "none" }}
                      >
                        Hop {hop.index}
                      </text>
                    </g>
                  );
                })}

                {layout.hops.map((hop, idx) => {
                  if (idx === layout.hops.length - 1) return null;
                  const nextHop = layout.hops[idx + 1];

                  return hop.nodes.flatMap((node, nodeIdx) => {
                    const keyNow = `${hop.index}-${nodeIdx}`;
                    const posNow = layout.positions[keyNow];
                    if (!posNow) return [];

                    return nextHop.nodes.map((nextNode, nextIdx) => {
                      const keyNext = `${nextHop.index}-${nextIdx}`;
                      const posNext = layout.positions[keyNext];
                      if (!posNext) return null;

                      const isTimeout = node.timeout || nextNode.timeout;
                      const stroke = isTimeout ? "#dddddd" : "#bbbbbb";

                      return (
                        <line
                          key={`${keyNow}-${keyNext}`}
                          x1={posNow.x}
                          y1={posNow.y}
                          x2={posNext.x}
                          y2={posNext.y}
                          stroke={stroke}
                          strokeWidth={1}
                        />
                      );
                    });
                  });
                })}

                {layout.hops.map((hop) =>
                  hop.nodes.map((node, nodeIdx) => {
                    const key = `${hop.index}-${nodeIdx}`;
                    const pos = layout.positions[key];
                    if (!pos) return null;

                    const isTimeout = node.timeout;
                    const rtt = node.rtt_ms ?? 0;

                    let fill = NORMAL_NODE_FILL;
                    if (isTimeout) fill = TIMEOUT_NODE_FILL;
                    else if (rtt >= HIGH_RTT_THRESHOLD_MS) fill = HIGH_RTT_NODE_FILL;

                    return (
                      <g
                        key={key}
                        onMouseEnter={(e) => showTooltip(e, hop.index, node, fill)}
                        onMouseMove={moveTooltip}
                        onMouseLeave={hideTooltip}
                        style={{ cursor: "pointer" }}
                      >
                        <circle cx={pos.x} cy={pos.y} r={NODE_RADIUS} fill={fill} stroke="#333" strokeWidth={1} />
                        <text
                          x={pos.x}
                          y={pos.y + 4}
                          textAnchor="middle"
                          fontSize={10}
                          fill="#ffffff"
                          style={{ pointerEvents: "none" }}
                        >
                          {hop.index}
                        </text>
                      </g>
                    );
                  })
                )}
              </svg>
            </div>

            {tooltip?.visible && (
              <div
                style={{
                  position: "absolute",
                  left: tooltip.x,
                  top: tooltip.y,
                  zIndex: 50,
                  minWidth: 220,
                  maxWidth: 320,
                  background: "rgba(20, 20, 20, 0.92)",
                  color: "#ffffff",
                  border: "1px solid rgba(255, 255, 255, 0.15)",
                  borderRadius: 10,
                  padding: "10px 12px",
                  boxShadow: "0 10px 20px rgba(0,0,0,0.25), 0 2px 6px rgba(0,0,0,0.18)",
                  fontSize: 12,
                  lineHeight: 1.35,
                  pointerEvents: "none",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 999,
                      background: tooltip.fill,
                      border: "1px solid rgba(255,255,255,0.25)",
                      display: "inline-block",
                      flex: "0 0 auto",
                    }}
                  />
                  <strong style={{ fontSize: 13 }}>Hop {tooltip.hopIndex}</strong>
                  {tooltip.isTimeout && (
                    <span style={{ marginLeft: "auto", color: TIMEOUT_TEXT_COLOR, fontSize: 12 }}>timeout</span>
                  )}
                </div>

                <div style={{ marginTop: 8 }}>
                  <div>
                    <span style={{ opacity: 0.8 }}>IP:</span>{" "}
                    <span style={{ fontFamily: "monospace" }}>{tooltip.node.ip}</span>
                  </div>

                  {!tooltip.isTimeout && (
                    <div>
                      <span style={{ opacity: 0.8 }}>RTT:</span>{" "}
                      <span style={{ fontFamily: "monospace" }}>
                        {tooltip.node.rtt_ms !== undefined ? `${tooltip.node.rtt_ms.toFixed(3)} ms` : "N/A"}
                      </span>
                      {tooltip.node.rtt_ms !== undefined && tooltip.node.rtt_ms >= HIGH_RTT_THRESHOLD_MS && (
                        <span style={{ marginLeft: 8, color: "#ffb3b3" }}>high</span>
                      )}
                    </div>
                  )}
                </div>

                <div style={{ marginTop: 8, opacity: 0.7, fontSize: 11 }}>Hover node to view details</div>
              </div>
            )}
          </div>

          <div className="mt-2">
            <small className="text-muted">
              Each column is one hop. Within a hop, multiple IPs are stacked vertically. Gray nodes mean timeout (*),
              red nodes mean high RTT (&gt;= {HIGH_RTT_THRESHOLD_MS} ms).
            </small>
          </div>
        </div>
      )}
    </div>
  );
};

export default TraceroutePanel;
